import React, { useState, useEffect, forwardRef, useImperativeHandle, ReactNode } from 'react';
import { Box, Image, ListItem, List, Flex } from '@chakra-ui/react';
import { User } from './mentions';

const MentionListItemText = (props: { bold?: boolean; children: ReactNode }) => {
   return (
      <Box
         as="span"
         fontSize="md"
         fontWeight={props.bold ? 'bold' : ''}
         paddingRight={2.5}
         color="black"
      >
         {props.children}
      </Box>
   );
};

const MentionListItem = (props: {
   index: number;
   item: User;
   selected: boolean;
   onMouseOver: () => void;
   onClick: () => void;
}) => {
   return (
      <ListItem textAlign="start" overflow="auto">
         <Flex
            as="button"
            alignItems="center"
            width="100%"
            paddingY={1.5}
            backgroundColor={props.selected ? 'gray.200' : 'white'}
            onClick={props.onClick}
            onMouseOver={props.onMouseOver}
         >
            <Image
               src={props.item.image}
               position="relative"
               boxSize={6}
               marginTop={0.5}
               marginRight={2}
               borderRadius="full"
            />
            <MentionListItemText bold={true}>{props.item.unique_username}</MentionListItemText>
            <MentionListItemText>{props.item.username}</MentionListItemText>
         </Flex>
      </ListItem>
   );
};

const MentionList = forwardRef(
   (props: { children?: ReactNode; items: User[]; command: (arg0: object) => void }, ref) => {
      const [selectedIndex, setSelectedIndex] = useState(0);

      const selectItem = (index: number) => {
         const item = props.items[index];

         if (item) {
            props.command({ id: item.unique_username });
         }
      };

      const upHandler = () => {
         setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
      };

      const downHandler = () => {
         setSelectedIndex((selectedIndex + 1) % props.items.length);
      };

      const enterHandler = () => {
         selectItem(selectedIndex);
      };

      useEffect(() => setSelectedIndex(0), [props.items]);

      useImperativeHandle(ref, () => ({
         onKeyDown: ({ event }) => {
            if (event.key === 'ArrowUp') {
               upHandler();
               return true;
            }

            if (event.key === 'ArrowDown') {
               downHandler();
               return true;
            }

            if (event.key === 'Enter') {
               enterHandler();
               return true;
            }

            return false;
         },
      }));

      return (
         <List
            borderRadius="base"
            paddingY={1}
            boxShadow={'0 2px 6px rgba(0, 0, 0, 0.3)'}
            backgroundColor="white"
            margin={0}
            padding={0}
         >
            {props.items.map((item: User, index: number) => (
               <MentionListItem
                  key={index}
                  index={index}
                  item={item}
                  selected={index === selectedIndex}
                  onClick={() => selectItem(index)}
                  onMouseOver={() => setSelectedIndex(index)}
               />
            ))}
         </List>
      );
   }
);

MentionList.displayName = 'MentionList';

export { MentionList };
